import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Store, Copy, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Restaurant Manager (POS account) management for admin.
 * Each manager can be linked to one OR more restaurants — they will see a
 * dropdown selector inside /pos to switch between stores.
 */
export const RestaurantManagersTab = ({ token }) => {
  const [managers, setManagers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', restaurant_ids: [] });
  const [busy, setBusy] = useState(false);

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/admin/restaurant-managers`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/restaurants`),
      ]);
      if (r1.ok) setManagers(await r1.json());
      if (r2.ok) setRestaurants(await r2.json());
    } catch (e) { console.debug('restaurant-managers load failed', e); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const toggleRid = (rid) => {
    setForm((prev) => {
      const has = prev.restaurant_ids.includes(rid);
      return { ...prev, restaurant_ids: has ? prev.restaurant_ids.filter(x => x !== rid) : [...prev.restaurant_ids, rid] };
    });
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password || form.restaurant_ids.length === 0) {
      toast.error('Preencha todos os campos e escolha pelo menos um restaurante');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password com pelo menos 6 caracteres');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/restaurant-managers`, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.promoted) {
          toast.success(`✅ ${data.message}`);
        } else if (data.user) {
          toast.success(data.message || 'Gestor criado/atualizado');
        }
        setForm({ name: '', email: '', password: '', restaurant_ids: [] });
        setShowForm(false);
        load();
      } else {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Erro ao criar');
      }
    } catch (e) {
      toast.error('Erro de rede');
    }
    setBusy(false);
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminar gestor "${m.name}"?`)) return;
    const r = await fetch(`${API_URL}/api/admin/restaurant-managers/${m.user_id}`, {
      method: 'DELETE', headers: headers(), credentials: 'include',
    });
    if (r.ok) {
      toast.success('Gestor eliminado');
      setManagers((prev) => prev.filter((x) => x.user_id !== m.user_id));
    }
  };

  const copyLoginInfo = (m) => {
    const url = `${window.location.origin}/pos`;
    const text = `WEGOO POS — ${m.restaurant_name || ''}\nURL: ${url}\nEmail: ${m.email}\nPassword: (definida ao criar)`;
    navigator.clipboard?.writeText(text);
    toast.success('Instruções copiadas');
  };

  return (
    <Card data-testid="restaurant-managers-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Store size={18} /> Gestores de Restaurante (POS)
          </CardTitle>
          <p className="text-xs text-[#9FA2B4] mt-1">
            Cada gestor faz login em <code className="bg-[#F4F1EA] px-1 rounded">/pos</code> ou na app Android e só vê os pedidos do seu restaurante.
          </p>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
          data-testid="open-create-manager"
        >
          <Plus size={16} className="mr-1" /> {showForm ? 'Cancelar' : 'Criar Gestor'}
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {showForm && (
          <div className="bg-[#FFF7ED] border border-[#FF6B35]/20 rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Restaurantes * (selecione um ou vários)</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white rounded-xl border border-[#F4F1EA]">
                  {restaurants.length === 0 && (
                    <p className="text-xs text-[#9FA2B4] col-span-full">Sem restaurantes disponíveis.</p>
                  )}
                  {restaurants.map((r) => {
                    const checked = form.restaurant_ids.includes(r.restaurant_id);
                    return (
                      <label
                        key={r.restaurant_id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm ${checked ? 'bg-[#FF6B35]/10 border border-[#FF6B35]/30' : 'hover:bg-[#FAFAF7] border border-transparent'}`}
                        data-testid={`manager-restaurant-${r.restaurant_id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleRid(r.restaurant_id)}
                        />
                        <span className="truncate">{r.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Nome do gestor *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="João Silva"
                  data-testid="manager-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Email (login) *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                  placeholder="pos@meurestaurante.pt"
                  data-testid="manager-email-input"
                />
                <p className="text-[11px] text-[#2EC4B6] mt-1">
                  💡 Se este email já existir como cliente, será automaticamente promovido a gestor.
                </p>
              </div>
              <div>
                <Label className="text-xs">Password * (mín. 6)</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••"
                    data-testid="manager-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9FA2B4]"
                    onClick={() => setShowPwd((v) => !v)}
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>
            <Button
              onClick={create}
              disabled={busy}
              className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
              data-testid="manager-create-submit"
            >
              {busy ? 'A criar...' : 'Criar gestor'}
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-[#9FA2B4]">A carregar...</p>
        ) : managers.length === 0 ? (
          <p className="text-sm text-[#9FA2B4]">Ainda não existem gestores. Clique em "Criar Gestor" para adicionar o primeiro.</p>
        ) : (
          <div className="space-y-2">
            {managers.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between gap-3 p-3 border border-[#F4F1EA] rounded-xl hover:bg-[#FAFAF7]"
                data-testid={`manager-row-${m.user_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#004E64] truncate">{m.name}</p>
                  <p className="text-xs text-[#9FA2B4] truncate">
                    {m.email} · <span className="text-[#FF6B35] font-medium">{m.restaurant_name}</span>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyLoginInfo(m)}
                  title="Copiar instruções de login"
                  data-testid={`manager-copy-${m.user_id}`}
                >
                  <Copy size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => remove(m)}
                  data-testid={`manager-delete-${m.user_id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RestaurantManagersTab;
